/**
 * T-459 — Scheda tecnica (PDF) per prodotti (foods) e ricette (recipes).
 *
 * Ogni articolo può avere UNA sola scheda tecnica, memorizzata come PDF nel
 * bucket privato `tech-sheets` con path `<tenant_id>/<kind>/<entity_id>.pdf`.
 * L'utente può comporla da più pagine (foto scattate e/o file scelti: PDF o
 * immagini): le pagine vengono unite in un unico PDF client-side con pdf-lib
 * (stesso pattern di `receiptPdf.appendScan`) e caricate SOLO al salvataggio
 * (offline-first: nessun round-trip di rete durante l'editing).
 */
import { PDFDocument, type PDFImage } from 'pdf-lib';
import { getSupabase } from '../supabase.js';

/** Una pagina della scheda tecnica in attesa di essere unita nel PDF finale. */
export interface TechSheetPage {
  b64: string;
  mime: string; // image/* | application/pdf
}

/**
 * Stato controllato dal parent (come la foto in FoodEditorModal):
 * - `path`: path del PDF già salvato su Storage (null se assente).
 * - `pending`: nuove pagine da unire e caricare al salvataggio.
 * - `removed`: true se l'utente ha chiesto di rimuovere la scheda esistente.
 */
export interface TechSheetState {
  path: string | null;
  pending: TechSheetPage[];
  removed: boolean;
}

export const EMPTY_TECH_SHEET: TechSheetState = { path: null, pending: [], removed: false };

/** Stato iniziale a partire dal path salvato sull'articolo. */
export function techSheetFromSaved(path: string | null): TechSheetState {
  return { path, pending: [], removed: false };
}

// A4 portrait (pt) per le pagine-immagine.
const PAGE_W = 595;
const PAGE_H = 842;
const MARGIN = 24;

/** Decodifica una stringa base64 (senza prefisso data:) in bytes. */
function b64ToBytes(b64: string): Uint8Array {
  const clean = b64.includes(',') ? b64.slice(b64.indexOf(',') + 1) : b64;
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Copia i bytes in un ArrayBuffer "pulito" (BlobPart non ambiguo per TS strict). */
function bytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  return buf;
}

function loadImageElement(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image_decode_failed'));
    img.src = url;
  });
}

/**
 * Ricodifica un'immagine in JPEG baseline via canvas. Serve per i formati che
 * `pdf-lib` NON incorpora nativamente — WebP e HEIC/HEIF (tipici dei pick da
 * galleria iOS e di alcune fotocamere Android), oltre a JPEG CMYK/progressivi
 * che `embedJpg` rifiuta. Ritorna null se il runtime non ha il DOM (es. test) o
 * se l'immagine non è decodificabile dalla WebView. L'alpha viene appiattito su
 * bianco (una scheda tecnica non ha trasparenza significativa).
 */
async function transcodeToJpeg(bytes: Uint8Array, mime: string): Promise<Uint8Array | null> {
  if (typeof document === 'undefined' || typeof Image === 'undefined' || typeof URL === 'undefined') {
    return null;
  }
  const blob = new Blob([bytesToArrayBuffer(bytes)], { type: mime || 'image/jpeg' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImageElement(url);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    if (canvas.width === 0 || canvas.height === 0) return null;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    return b64ToBytes(canvas.toDataURL('image/jpeg', 0.9));
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Incorpora un'immagine nel PDF scegliendo l'encoder giusto per il mime, con
 * fallback a una ricodifica JPEG via canvas (WebP/HEIC/CMYK/progressivi). NON
 * fa "silent drop" dei formati moderni: quello resta solo per l'immagine
 * realmente non decodificabile dalla WebView. Ritorna null se non incorporabile.
 */
async function embedImage(pdf: PDFDocument, bytes: Uint8Array, mime: string): Promise<PDFImage | null> {
  const lower = (mime || '').toLowerCase();
  if (lower === 'image/png') {
    try { return await pdf.embedPng(bytes); } catch { /* prova via canvas sotto */ }
  } else if (lower === 'image/jpeg' || lower === 'image/jpg') {
    try { return await pdf.embedJpg(bytes); } catch { /* prova via canvas sotto */ }
  }
  const jpeg = await transcodeToJpeg(bytes, lower);
  if (!jpeg) return null;
  try { return await pdf.embedJpg(jpeg); } catch { return null; }
}

/**
 * Unisce le pagine in un unico PDF:
 * - pagina PDF → carica il documento e copia TUTTE le sue pagine;
 * - pagina immagine → embed in una pagina A4 portrait, scalata "contain"
 *   con margine ~24pt.
 */
export async function buildTechSheetPdf(pages: TechSheetPage[]): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setCreator('TrackEAT');
  pdf.setProducer('TrackEAT');

  for (const page of pages) {
    const bytes = b64ToBytes(page.b64);
    if (page.mime === 'application/pdf') {
      try {
        const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const copied = await pdf.copyPages(src, src.getPageIndices());
        for (const p of copied) pdf.addPage(p);
      } catch {
        // PDF non incorporabile → salta questa pagina senza rompere il resto.
      }
      continue;
    }

    // Immagine: A4 portrait con drawImage centrata e scalata "contain".
    // embedImage sceglie PNG/JPEG o ricodifica via canvas (WebP/HEIC/…).
    const img = await embedImage(pdf, bytes, page.mime);
    if (!img) continue; // formato non decodificabile dalla WebView → salta
    const sheet = pdf.addPage([PAGE_W, PAGE_H]);
    const availW = PAGE_W - 2 * MARGIN;
    const availH = PAGE_H - 2 * MARGIN;
    const scale = Math.min(availW / img.width, availH / img.height, 1);
    const w = img.width * scale;
    const h = img.height * scale;
    sheet.drawImage(img, {
      x: MARGIN + (availW - w) / 2,
      y: MARGIN + (availH - h) / 2,
      width: w,
      height: h,
    });
  }

  return pdf.save();
}

/**
 * Applica lo stato al Storage e ritorna il path definitivo da salvare
 * sull'articolo (o null se la scheda è stata rimossa / non esiste):
 * - pending non vuoto → costruisce il PDF e fa upsert (una sola scheda per
 *   articolo, contentType application/pdf);
 * - removed su un path esistente → rimuove l'oggetto e ritorna null;
 * - altrimenti → ritorna il path invariato.
 */
export async function commitTechSheet(
  kind: 'foods' | 'recipes',
  tenantId: string,
  entityId: string,
  st: TechSheetState,
): Promise<string | null> {
  const sb = getSupabase();
  if (st.pending.length > 0) {
    const bytes = await buildTechSheetPdf(st.pending);
    const path = `${tenantId}/${kind}/${entityId}.pdf`;
    // Copy into a plain ArrayBuffer so the bytes are an unambiguous BlobPart
    // (TS strict typed-array generics reject Uint8Array<ArrayBufferLike> here).
    const body = new Blob([bytesToArrayBuffer(bytes)], { type: 'application/pdf' });
    const { error } = await sb.storage.from('tech-sheets').upload(path, body, {
      contentType: 'application/pdf',
      upsert: true,
    });
    if (error) throw error;
    return path;
  }
  if (st.removed && st.path) {
    const { error } = await sb.storage.from('tech-sheets').remove([st.path]);
    if (error) throw error;
    return null;
  }
  return st.path;
}

/** Apre la scheda tecnica salvata in un visualizzatore in-app (URL firmato 15'). */
export async function openTechSheet(path: string): Promise<void> {
  const sb = getSupabase();
  const { data, error } = await sb.storage.from('tech-sheets').createSignedUrl(path, 60 * 15);
  if (error) throw error;
  const url = data?.signedUrl;
  if (!url) throw new Error('Impossibile aprire la scheda tecnica: URL non disponibile.');
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url, presentationStyle: 'fullscreen' });
}
